import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import clsx from 'clsx';
import { Bot as BotIcon, Check, LoaderCircle, X, type LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import type { PresenceStatus } from '@spawnwire/shared';

export { clsx };

// ---- Buttons ----------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: LucideIcon;
}

const variantClass: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover',
  secondary: 'bg-active text-fg hover:bg-line-strong',
  ghost: 'text-muted hover:bg-hover hover:text-fg',
  danger: 'bg-danger text-white hover:brightness-110',
  link: 'text-accent hover:underline px-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon: Icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-8 px-3 text-sm' : size === 'lg' ? 'h-11 px-5 text-base' : 'h-9 px-4 text-sm',
        variantClass[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <LoaderCircle className="size-4 animate-spin" aria-hidden /> : Icon ? <Icon className="size-4" aria-hidden /> : null}
      {children}
    </button>
  );
});

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  danger?: boolean;
  size?: 'sm' | 'md';
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, active, danger, size = 'md', className, tooltipSide = 'top', ...rest },
  ref,
) {
  return (
    <Tooltip label={label} side={tooltipSide}>
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-pressed={active}
        className={clsx(
          'inline-flex shrink-0 items-center justify-center rounded-md transition-colors disabled:opacity-40',
          size === 'sm' ? 'size-7' : 'size-8',
          danger ? 'text-danger hover:bg-danger-soft' : active ? 'bg-active text-fg' : 'text-muted hover:bg-hover hover:text-fg',
          className,
        )}
        {...rest}
      >
        <Icon className={size === 'sm' ? 'size-4' : 'size-[18px]'} aria-hidden />
      </button>
    </Tooltip>
  );
});

// ---- Form controls --------------------------------------------------------------------

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return (
    <input
      ref={ref}
      className={clsx(
        'h-10 w-full rounded-md border border-line bg-input px-3 text-sm text-fg placeholder:text-faint outline-none transition focus:border-accent',
        className,
      )}
      {...rest}
    />
  );
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={clsx('w-full resize-none rounded-md border border-line bg-input px-3 py-2 text-sm text-fg placeholder:text-faint outline-none focus:border-accent', className)}
      {...rest}
    />
  );
});

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-wide text-muted">
        {label}
      </label>
      {children}
      {error ? <p className="text-xs text-danger">{error}</p> : hint ? <p className="text-xs text-faint">{hint}</p> : null}
    </div>
  );
}

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string; disabled?: boolean }) {
  return (
    <label className={clsx('flex items-start justify-between gap-4 py-2', disabled && 'opacity-50')}>
      <span>
        <span className="block text-sm font-medium text-fg">{label}</span>
        {description && <span className="mt-0.5 block text-xs text-muted">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx('relative mt-0.5 h-6 w-10 shrink-0 rounded-full transition-colors', checked ? 'bg-accent' : 'bg-line-strong')}
      >
        <span className={clsx('absolute top-0.5 left-0 size-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-[18px]' : 'translate-x-0.5')} />
      </button>
    </label>
  );
}

export function Select<T extends string>({
  value,
  onChange,
  options,
  id,
  className,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  id?: string;
  className?: string;
}) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className={clsx('h-10 w-full rounded-md border border-line bg-input px-3 text-sm text-fg outline-none focus:border-accent', className)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return <LoaderCircle className={clsx('animate-spin text-muted', className ?? 'size-5')} aria-label={label} role="status" />;
}

export function EmptyState({ icon: Icon, title, body, action }: { icon: LucideIcon; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-active text-muted">
        <Icon className="size-7" aria-hidden />
      </div>
      <div className="text-base font-semibold text-fg">{title}</div>
      {body && <p className="max-w-sm text-sm text-muted">{body}</p>}
      {action}
    </div>
  );
}

export function Badge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={clsx('inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white', className)}
      aria-label={`${count} unread mention${count === 1 ? '' : 's'}`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Marks Spawnwire's own system account next to its name. */
export function SystemTag({ className }: { className?: string }) {
  return (
    <span className={clsx('ml-1.5 inline-flex translate-y-[-1px] items-center gap-0.5 rounded bg-accent px-1 py-px align-middle text-[10px] font-bold uppercase leading-tight text-accent-fg', className)}>
      <Check className="size-2.5" strokeWidth={3} aria-hidden />
      System
    </span>
  );
}

/** Marks a bot account next to its name. The server sets `bot`; nothing a bot sends can turn it on or off. */
export function BotTag({ className }: { className?: string }) {
  return (
    <span
      className={clsx('ml-1.5 inline-flex shrink-0 translate-y-[-1px] items-center gap-0.5 rounded bg-accent px-1 py-px align-middle text-[10px] font-bold uppercase leading-tight text-accent-fg', className)}
      title="This account is a bot"
    >
      <BotIcon className="size-2.5" strokeWidth={3} aria-hidden />
      Bot
    </span>
  );
}

/** The badge a user's name carries, if any. */
export function UserBadge({ user, className }: { user: { system?: boolean; bot?: boolean } | null | undefined; className?: string }) {
  if (user?.system) return <SystemTag className={className} />;
  if (user?.bot) return <BotTag className={className} />;
  return null;
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-line-strong bg-active px-1.5 py-0.5 font-mono text-[11px] text-muted">{children}</kbd>;
}

// ---- Avatar and presence --------------------------------------------------------------

const AVATAR_COLORS = ['#2fbf9f', '#e0703f', '#6b8cff', '#d65db1', '#e0b23f', '#4fb0d6', '#8f6be0', '#5fb85f'];

export function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts.length === 1 ? parts[0]!.slice(0, 2) : parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

const statusColor: Record<PresenceStatus, string> = {
  online: 'bg-online',
  idle: 'bg-idle',
  dnd: 'bg-dnd',
  offline: 'bg-offline',
};

export const statusLabel: Record<PresenceStatus, string> = { online: 'Online', idle: 'Idle', dnd: 'Do not disturb', offline: 'Offline' };

export function StatusDot({ status, className, ring = 'ring-sidebar' }: { status: PresenceStatus; className?: string; ring?: string }) {
  return <span className={clsx('block rounded-full ring-[3px]', ring, statusColor[status], className ?? 'size-3')} role="img" aria-label={statusLabel[status]} title={statusLabel[status]} />;
}

export function Avatar({
  user,
  size = 40,
  status,
  speaking,
  ringClass,
  className,
}: {
  user: { id: string; displayName: string; avatarUrl: string | null; system?: boolean } | null | undefined;
  size?: number;
  status?: PresenceStatus | null;
  speaking?: boolean;
  ringClass?: string;
  className?: string;
}) {
  const name = user?.displayName ?? '?';
  const dot = Math.max(10, Math.round(size * 0.32));
  // Spawnwire's system account wears the app icon.
  const src = user?.avatarUrl ?? (user?.system ? '/icon-192.png' : null);
  return (
    <span className={clsx('relative inline-block shrink-0', className)} style={{ width: size, height: size }}>
      {src ? (
        <img src={src} alt="" className={clsx('size-full rounded-full object-cover', speaking && 'speaking-ring')} draggable={false} />
      ) : (
        <span
          className={clsx('flex size-full items-center justify-center rounded-full font-semibold text-white select-none', speaking && 'speaking-ring')}
          style={{ background: user ? colorFor(user.id) : '#6d7480', fontSize: size * 0.38 }}
          aria-hidden
        >
          {initials(name)}
        </span>
      )}
      {status && (
        <span className="absolute -bottom-0.5 -right-0.5" style={{ width: dot, height: dot }}>
          <StatusDot status={status} className="size-full" ring={ringClass} />
        </span>
      )}
    </span>
  );
}

/** Profile banner: the uploaded image if there is one, otherwise the chosen (or generated) colour. */
export function ProfileBanner({
  user,
  color,
  className,
}: {
  user: { id: string; bannerColor: string | null; bannerUrl: string | null } | null | undefined;
  /** Overrides the colour, e.g. an unsaved pick in settings. */
  color?: string;
  className?: string;
}) {
  const background = color ?? user?.bannerColor ?? (user ? colorFor(user.id) : 'var(--p-active)');
  return (
    <div className={clsx('relative overflow-hidden', className)} style={{ background }}>
      {user?.bannerUrl && <img src={user.bannerUrl} alt="" className="absolute inset-0 size-full object-cover" draggable={false} />}
    </div>
  );
}

// ---- Overlays ---------------------------------------------------------------------------

export function Tooltip({ label, children, side = 'top' }: { label: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <TooltipPrimitive.Root delayDuration={300}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content side={side} sideOffset={6} className="animate-fade z-[80] rounded-md bg-elevated px-2.5 py-1.5 text-xs font-medium text-fg shadow-pop border border-line">
          {label}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const TooltipProvider = TooltipPrimitive.Provider;

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="animate-fade fixed inset-0 z-[60] bg-black/60" />
        <DialogPrimitive.Content
          className={clsx(
            'animate-pop fixed left-1/2 top-1/2 z-[61] flex max-h-[90vh] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-line bg-overlay shadow-pop outline-none',
            size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-2xl' : 'max-w-md',
          )}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-5">
            <div>
              <DialogPrimitive.Title className="text-lg font-semibold text-fg">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-muted">{description}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <button type="button" className="rounded-md p-1 text-muted hover:bg-hover hover:text-fg" aria-label="Close">
                <X className="size-5" />
              </button>
            </DialogPrimitive.Close>
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex justify-end gap-2 rounded-b-xl border-t border-line bg-sidebar px-5 py-3">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function FullscreenDialog({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content className="animate-fade fixed inset-0 z-[55] flex bg-main outline-none" aria-describedby={undefined}>
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ---- Menus ---------------------------------------------------------------------------------

export const menuContent = 'animate-pop z-[70] min-w-[200px] rounded-lg border border-line bg-overlay p-1.5 shadow-pop';
export const menuItem =
  'flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-fg data-[disabled]:pointer-events-none data-[disabled]:opacity-40';
const menuDanger = 'text-danger data-[highlighted]:bg-danger data-[highlighted]:text-white';

export interface MenuEntry {
  label: string;
  icon?: LucideIcon;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  shortcut?: string;
  separatorBefore?: boolean;
  hidden?: boolean;
}

function renderEntries(entries: MenuEntry[], Item: typeof DropdownPrimitive.Item | typeof ContextMenuPrimitive.Item, Sep: typeof DropdownPrimitive.Separator) {
  return entries
    .filter((e) => !e.hidden)
    .map((e, i) => (
      <div key={`${e.label}-${i}`}>
        {e.separatorBefore && i > 0 && <Sep className="my-1 h-px bg-line" />}
        <Item className={clsx(menuItem, e.danger && menuDanger)} disabled={e.disabled} onSelect={() => e.onSelect?.()}>
          {e.icon && <e.icon className="size-4 shrink-0" aria-hidden />}
          <span className="flex-1">{e.label}</span>
          {e.shortcut && <span className="text-xs opacity-60">{e.shortcut}</span>}
        </Item>
      </div>
    ));
}

export function DropdownMenu({ trigger, entries, align = 'end', side = 'bottom' }: { trigger: ReactNode; entries: MenuEntry[]; align?: 'start' | 'end' | 'center'; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <DropdownPrimitive.Root modal={false}>
      <DropdownPrimitive.Trigger asChild>{trigger}</DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content className={menuContent} align={align} side={side} sideOffset={6}>
          {renderEntries(entries, DropdownPrimitive.Item, DropdownPrimitive.Separator)}
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}

export function ContextMenu({ children, entries, disabled }: { children: ReactNode; entries: MenuEntry[]; disabled?: boolean }) {
  if (disabled || entries.filter((e) => !e.hidden).length === 0) return <>{children}</>;
  return (
    <ContextMenuPrimitive.Root modal={false}>
      <ContextMenuPrimitive.Trigger asChild>{children}</ContextMenuPrimitive.Trigger>
      <ContextMenuPrimitive.Portal>
        <ContextMenuPrimitive.Content className={menuContent}>
          {renderEntries(entries, ContextMenuPrimitive.Item, ContextMenuPrimitive.Separator as unknown as typeof DropdownPrimitive.Separator)}
        </ContextMenuPrimitive.Content>
      </ContextMenuPrimitive.Portal>
    </ContextMenuPrimitive.Root>
  );
}
